import { writable } from 'svelte/store';

/**
 * Creates a Svelte store that syncs its value to localStorage, if available,
 * otherwise it falls back to in-memory storage.
 * @template T
 * @param {string} key - The localStorage key to use for storing the data.
 * @param {T} initialValue - Initial value of the store if localStorage is empty or unavailable.
 * @param {object} [options] - Optional persistence behavior.
 * @param {(value: T) => T} [options.sanitize] - Maps a value to the form written to localStorage.
 * Applied to every write and to the rehydrated value, so session-only fields can be reset on disk
 * (and scrubbed from values persisted by an earlier session) while the live store keeps them.
 * @returns {import('svelte/store').Writable<T>} A Svelte writable store of type T
 */
function createPersistentStore(key, initialValue, options = {}) {
	const { sanitize } = options;
	/**
	 * @param {T} value - Live store value.
	 * @returns {T} Value safe to serialize.
	 */
	const toPersistedForm = (value) => (sanitize ? sanitize(value) : value);
	const isLocalStorageAvailable = typeof localStorage !== 'undefined';

	let data = initialValue;
	if (isLocalStorageAvailable) {
		const storedValue = localStorage.getItem(key);
		if (storedValue) {
			data = toPersistedForm(JSON.parse(storedValue));
		}
	}

	const store = writable(data);

	store.subscribe((value) => {
		// localStorage can become unavailable (at least on Safari) during page reloads
		if (typeof localStorage !== 'undefined') {
			localStorage.setItem(key, JSON.stringify(toPersistedForm(value)));
		}
		// Optionally, handle the fallback storage update here if needed
	});

	return store;
}

export default createPersistentStore;
